import { useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { DarkModeToggle } from "../components/DarkModeToggle";

export default function TermsConditions() {
  const nav = useNavigate();

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-[#0c1120]">
      <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 px-4 py-3 sticky top-0 z-40">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <button data-testid="terms-back-btn" onClick={() => nav(-1)} className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800">
            <ChevronLeft className="w-5 h-5 text-slate-500" />
          </button>
          <h1 className="text-lg font-extrabold text-slate-900 dark:text-white flex-1">Terms & Conditions</h1>
          <DarkModeToggle />
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6 sm:p-8 space-y-8">

          <div>
            <p className="text-xs text-slate-400 mb-1">Version 1.0 — Effective April 2026</p>
            <h2 className="text-2xl font-extrabold text-slate-900 dark:text-white">National Driving Guild</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-2 leading-relaxed">
              These Terms & Conditions govern your use of National Driving Guild ("the Platform", "we", "us"). By creating an account, you agree to be bound by these terms in full. You can return to this page at any time to read them again.
            </p>
          </div>

          <Section title="1. Lesson Booking Rules">
            <p>All driving lessons must be booked through the Platform. Lessons are subject to instructor availability and are confirmed only after successful payment. By booking a lesson, you are entering into an agreement for that specific date, time, and duration.</p>
          </Section>

          <Section title="2. Prepayment Requirement">
            <p>All lessons must be prepaid through the Platform using online card payment (Stripe). No lessons may be reserved without payment. Payment must be received at the time of booking to secure your lesson slot.</p>
          </Section>

          <Section title="3. Cancellation Policy (24 Hours)">
            <p>Lessons cancelled <strong>more than 24 hours</strong> before the scheduled start time are eligible for a full refund or account credit (as determined by your instructor's policy).</p>
            <p className="mt-2">Lessons cancelled <strong>within 24 hours</strong> of the scheduled start time may be non-refundable and charged in full. This includes cancellations made for any reason, including illness or personal circumstances. Your instructor may override this at their discretion.</p>
            <p className="mt-2">Learners are required to acknowledge and accept this cancellation policy at the time of booking.</p>
          </Section>

          <Section title="3a. Automatic Payment (72 Hours)">
            <p>To confirm your lesson, payment is taken automatically <strong>72 hours before</strong> the scheduled start time. This is separate from cancellation: you can still cancel free of charge up to 24 hours before the lesson and receive a refund or credit.</p>
          </Section>

          <Section title="4. Refund & Credit Policy">
            <p>Where a cancellation is eligible for a refund or credit:</p>
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li><strong>Account Credit</strong> — the lesson fee is credited to your Platform account for future bookings.</li>
              <li><strong>Refund</strong> — the lesson fee is returned to your original payment method, subject to processing times.</li>
            </ul>
            <p className="mt-2">The method of refund (credit or refund) is determined by the instructor or administrator settings and may vary.</p>
          </Section>

          <Section title="5. No-Show Policy">
            <p>If you fail to attend a booked lesson without prior cancellation ("no-show"), the full lesson fee is forfeited. No refund or credit will be issued for no-shows.</p>
          </Section>

          <Section title="6. Lateness Policy">
            <p>If you arrive late for a lesson, the lesson will end at the originally scheduled time. No extension or refund will be given for time lost due to learner lateness. Your instructor may, at their discretion, adjust the lesson content but is not obligated to extend the session.</p>
          </Section>

          <Section title="7. Instructor Cancellation & Rescheduling">
            <p>Instructors reserve the right to cancel or reschedule a lesson due to illness, vehicle issues, severe weather, or other circumstances beyond their control. Where an instructor cancels:</p>
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li>You will be offered an alternative time or a full refund/credit.</li>
              <li>Instructors will give as much notice as reasonably possible.</li>
              <li>The Platform is not liable for losses or inconvenience caused by instructor cancellations.</li>
            </ul>
          </Section>

          <Section title="8. Learner Responsibilities">
            <p>As a learner using this Platform, you confirm that:</p>
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li>You hold a valid UK provisional or full driving licence (as applicable).</li>
              <li>You meet the minimum eyesight requirements for driving as set by the DVSA.</li>
              <li>You are medically fit to drive and will inform your instructor of any conditions that may affect your ability to drive safely.</li>
              <li>You will attend lessons in appropriate footwear and clothing.</li>
              <li>You will inform your instructor immediately if you feel unwell or unsafe during a lesson.</li>
            </ul>
          </Section>

          <Section title="9. Respectful Behaviour">
            <p>All users of the Platform — learners, instructors, and staff — are expected to treat each other with respect and professionalism. We do not tolerate:</p>
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li>Abusive, threatening, or discriminatory language or behaviour.</li>
              <li>Harassment of any kind.</li>
              <li>Deliberate damage to vehicles or property.</li>
            </ul>
            <p className="mt-2">Violation of this policy may result in account suspension or termination without refund.</p>
          </Section>

          <Section title="10. Platform & App Usage">
            <p>The Platform is provided for the purpose of booking, managing, and supporting driving lessons. You agree not to:</p>
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li>Use the Platform for any unlawful purpose.</li>
              <li>Attempt to access accounts, data, or features not intended for you.</li>
              <li>Share your account credentials with others.</li>
              <li>Use automated tools, bots, or scrapers on the Platform.</li>
              <li>Upload harmful, offensive, or misleading content.</li>
            </ul>
          </Section>

          <Section title="11. Privacy & Data">
            <p>Your use of the Platform is also governed by our <a href="/privacy" className="text-blue-600 hover:text-blue-700 font-semibold underline">Privacy Policy</a>, which explains how we collect, store, and use your personal data. By accepting these Terms, you also acknowledge you have read and understood our Privacy Policy.</p>
          </Section>

          <Section title="12. Pricing Changes">
            <p>Lesson prices are set by instructors and may change from time to time. Any price changes will be communicated with reasonable notice. Bookings made before a price change will be honoured at the original price.</p>
          </Section>

          <Section title="13. Fraud, Misuse & Suspension">
            <p>We reserve the right to suspend or terminate your account without notice if we reasonably believe you have:</p>
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li>Provided false information during registration.</li>
              <li>Attempted payment fraud or chargebacks.</li>
              <li>Misused the Platform in a way that harms other users or the business.</li>
              <li>Violated these Terms & Conditions.</li>
            </ul>
            <p className="mt-2">Suspended accounts may not be eligible for refund of any remaining credits or prepaid lessons.</p>
          </Section>

          <Section title="14. Limitation of Liability">
            <p>The Platform is provided "as is" without warranty of any kind. To the maximum extent permitted by law:</p>
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li>We are not liable for the quality, outcome, or safety of driving instruction provided by instructors using the Platform.</li>
              <li>We are not responsible for test results, driving licence outcomes, or any loss arising from lessons booked through the Platform.</li>
              <li>Our total liability to you shall not exceed the amount you have paid through the Platform in the preceding 12 months.</li>
            </ul>
          </Section>

          <Section title="15. Communication Consent">
            <p>By creating an account, you consent to receive communications from us related to:</p>
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li>Booking confirmations and reminders.</li>
              <li>Lesson changes or cancellations.</li>
              <li>Account and payment notifications.</li>
              <li>Platform updates relevant to your account.</li>
            </ul>
            <p className="mt-2">You may opt out of non-essential communications at any time through your account settings.</p>
          </Section>

          <Section title="16. Contact">
            <p>If you have any questions about these Terms & Conditions, please contact us through the Platform or at the details provided in your account settings.</p>
          </Section>

          <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
            <p className="text-xs text-slate-400 leading-relaxed">
              These terms were last updated in April 2026. We may update these terms from time to time. Continued use of the Platform after changes constitutes acceptance of the updated terms.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h3 className="text-base font-extrabold text-slate-900 dark:text-white mb-2">{title}</h3>
      <div className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
        {children}
      </div>
    </div>
  );
}
